export interface MapViewOverlayOptions {
  state?: any
  onClick?: () => void
}

export class MapViewOverlay extends google.maps.OverlayView {
  private bounds: google.maps.LatLngBounds
  private image: string
  private div: HTMLElement
  private divEvent: HTMLElement

  private degree: number = 0
  private map: google.maps.Map

  private state: any
  private options?: MapViewOverlayOptions

  constructor(
    swBound: google.maps.LatLng,
    neBound: google.maps.LatLng,
    image: string,
    degree: number,
    map: google.maps.Map,
    options?: MapViewOverlayOptions,
  ) {
    super()

    const bounds = new google.maps.LatLngBounds(swBound, neBound)
    this.map = map
    this.setMap(map)
    this.bounds = bounds
    this.degree = degree
    this.image = image
    this.div = document.createElement('div')
    this.divEvent = document.createElement('div')
    this.state = options?.state
    this.options = options
  }

  /**
   * onAdd is called when the map's panes are ready and the overlay has been
   * added to the map.
   */

  getDiv() {
    return this.div
  }
  getDegree() {
    return this.degree
  }
  getImage() {
    return this.image
  }

  getState<T = any>() {
    return this.state as T
  }

  getOptions() {
    return this.options
  }

  getBase64FromUrl = async (url: string): Promise<string> => {
    const data = await fetch(url)
    const blob = await data.blob()
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.readAsDataURL(blob)
      reader.onloadend = () => {
        const base64data = reader.result as string
        resolve(base64data)
      }
    })
  }

  onAdd() {
    this.div.style.borderStyle = 'none'
    this.div.style.borderWidth = '0px'
    this.div.style.position = 'absolute'
    this.div.style.transform = 'rotate(' + this.degree + 'deg)'
    this.div.style.pointerEvents = 'none'

    const loading = document.createElement('div')
    loading.innerHTML = '<div></div>'
    loading.className = 'lds-dual-ring'
    this.div.appendChild(loading)
    this.getBase64FromUrl(this.image)
      .then((res) => {
        const img = document.createElement('img')
        img.src = res
        img.style.width = '100%'
        img.style.height = '100%'
        this.div.removeChild(loading)
        this.div.appendChild(img)
      })
      .catch(() => {
        const img = document.createElement('img')
        img.src = this.image
        img.style.width = '100%'
        img.style.height = '100%'
        this.div.removeChild(loading)
        this.div.appendChild(img)
      })
    this.setValues({
      position: this.getBounds().getCenter(),
    })
    this.set('container', this.div)
    this.divEvent = this.div.cloneNode() as HTMLElement
    this.divEvent.style.backgroundColor = 'transparent'
    const panes = this.getPanes()!
    panes.mapPane.appendChild(this.div)
    panes.overlayMouseTarget.appendChild(this.divEvent)
    if (this.options?.onClick) {
      this.divEvent.addEventListener('click', () => {
        this.options?.onClick && this.options.onClick()
      })
    }
  }

  getBounds() {
    return this.bounds
  }

  draw() {
    const overlayProjection = this.getProjection()
    const sw = overlayProjection.fromLatLngToDivPixel(
      this.bounds.getSouthWest(),
    )!
    const ne = overlayProjection.fromLatLngToDivPixel(
      this.bounds.getNorthEast(),
    )!
    if (this.div) {
      this.div.style.left = sw.x + 'px'
      this.div.style.top = ne.y + 'px'
      this.div.style.width = ne.x - sw.x + 'px'
      this.div.style.height = sw.y - ne.y + 'px'
    }
    if (this.divEvent) {
      this.divEvent.style.left = sw.x + 'px'
      this.divEvent.style.top = ne.y + 'px'
      this.divEvent.style.width = ne.x - sw.x + 'px'
      this.divEvent.style.height = sw.y - ne.y + 'px'
    }
  }
  onRemove() {
    if (this.divEvent) {
      ;(this.divEvent.parentNode as HTMLElement).removeChild(this.divEvent)
    }
    if (this.div) {
      ;(this.div.parentNode as HTMLElement).removeChild(this.div)
    }
  }

  clear() {
    this.div.remove()
    this.divEvent.remove()
  }

  hide() {
    if (this.div) {
      this.div.style.display = 'none'
    }
    if (this.divEvent) {
      this.divEvent.style.display = 'none'
    }
  }

  show() {
    if (this.div) {
      this.div.style.display = 'block'
    }
    if (this.divEvent) {
      this.divEvent.style.display = 'block'
    }
  }

  isShow() {
    return this.div?.style?.display !== 'none'
  }

  toggle() {
    if (this.div) {
      if (this.div.style.display === 'none') {
        this.show()
      } else {
        this.hide()
      }
    }
  }

  toggleDOM(map: google.maps.Map) {
    if (this.getMap()) {
      this.setMap(null)
    } else {
      this.setMap(map)
    }
  }

  updateBounds(bounds: google.maps.LatLngBounds) {
    this.bounds = bounds
    this.draw()
  }
}

export default MapViewOverlay
